import { PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { User, UsersState } from './users.types';
import { fetchUsers, removeUser, updateUserPrivileges, updateUser } from './users.thunks';
import { grantProjectPrivileges, revokeProjectPrivileges } from './project-privileges.thunks';

// Reducer for handling fetchUsers async thunk
export const fetchUsersReducer = (builder: ActionReducerMapBuilder<UsersState>): void => {
  builder
    .addCase(fetchUsers.pending, state => {
      state.status = 'loading';
      state.error = null; // Clear previous errors
    })
    .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UsersState['list']>) => {
      state.status = 'succeeded';
      state.list = action.payload;
    })
    .addCase(fetchUsers.rejected, (state, action) => {
      state.status = 'failed';
      // action.payload contains the error message from rejectWithValue
      state.error = action.payload;
    });
};

// Reducer for handling removeUser async thunk
export const removeUserReducer = (builder: ActionReducerMapBuilder<UsersState>): void => {
  builder
    .addCase(removeUser.pending, state => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(removeUser.fulfilled, (state, action: PayloadAction<string>) => {
      state.status = 'succeeded';
      // Remove the user from the list by filtering
      state.list = state.list.filter(user => user.login !== action.payload);
    })
    .addCase(removeUser.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
};

// Reducer for handling updateUserPrivileges async thunk
export const updateUserPrivilegesReducer = (builder: ActionReducerMapBuilder<UsersState>): void => {
  builder
    .addCase(updateUserPrivileges.pending, state => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(updateUserPrivileges.fulfilled, (state, action: PayloadAction<User>) => {
      state.status = 'succeeded';
      // Update the user in the list with the updated privileges
      state.list = state.list.map(user =>
        user.login === action.payload.login ? action.payload : user,
      );
    })
    .addCase(updateUserPrivileges.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
};

// Reducer for handling updateUser async thunk
export const updateUserReducer = (builder: ActionReducerMapBuilder<UsersState>): void => {
  builder
    .addCase(updateUser.pending, state => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
      state.status = 'succeeded';
      // Update the user in the list with the updated data
      state.list = state.list.map(user => {
        return user?.login === action.payload?.login ? action.payload : user;
      });
    })
    .addCase(updateUser.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
};

// Reducer for handling grantProjectPrivileges async thunk
export const grantProjectPrivilegesReducer = (
  builder: ActionReducerMapBuilder<UsersState>,
): void => {
  builder
    .addCase(grantProjectPrivileges.pending, state => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(grantProjectPrivileges.fulfilled, state => {
      state.status = 'succeeded';
      // We don't modify the list here - it would require a re-fetch to get the updated list
    })
    .addCase(grantProjectPrivileges.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
};

// Reducer for handling revokeProjectPrivileges async thunk
export const revokeProjectPrivilegesReducer = (
  builder: ActionReducerMapBuilder<UsersState>,
): void => {
  builder
    .addCase(revokeProjectPrivileges.pending, state => {
      state.status = 'loading';
      state.error = null;
    })
    .addCase(revokeProjectPrivileges.fulfilled, state => {
      state.status = 'succeeded';
      // We don't modify the list here - it would require a re-fetch to get the updated list
    })
    .addCase(revokeProjectPrivileges.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    });
};
